import {makeAutoObservable, runInAction} from 'mobx';
import {TRISTATE_CHECKBOX_VALUES} from '../../../tristate-checkbox/tristate-checkbox';
import CheckboxesTreeItemStore from "./checkboxes-tree-item-store";

/**
 * Plain model item for nested checkbox lists.
 */
export interface ICheckboxesTreeItem extends Record<string, any> {
    value: string;
    label: string;
    checkBoxValue?: TRISTATE_CHECKBOX_VALUES;
    childNodes?: ICheckboxesTreeItem[];
    disabled?: boolean;
}

/**
 * Manages nested checkbox items and their behavior: expansion, selection, and summary.
 * Holds item stores, emits normalized updates, and supports in-place visibility updates.
 */
class CheckboxesTreeStore {

    private onChangeCallback?: (updatedItems: ICheckboxesTreeItem[]) => void;
    private _childNodes: CheckboxesTreeItemStore[] = [];
    private _userExpandCollapseSettings: Map<string, boolean> = new Map<string, boolean>();
    private _expandAllPaths: Set<string> = new Set<string>();
    private _disabled: boolean = false;
    private _filterTerm: string = '';
    private _isFiltering: boolean = false;

    /** Initializes item stores and wires onChange callback. */
    public constructor() {
        makeAutoObservable(this, {}, {autoBind: true});
    }

    /** Updates external onChange callback. */
    public setOnChangeCallback(callback?: (updatedItems: ICheckboxesTreeItem[]) => void): void {
        this.onChangeCallback = callback;
    }

    /** Enables or disables global expand-all behavior derived from filter state. */
    public isAllExpanded(enabled: boolean): void {
        this._expandAllPaths = enabled ? this.pathsByExpandedAllFromStores(this._childNodes, true) : new Set<string>();
    }

    /** Returns root item stores. */
    public getItems(): CheckboxesTreeItemStore[] {
        return this._childNodes;
    }

    /** Returns whether a list is disabled. */
    public isDisabled(): boolean {
        return this._disabled;
    }

    /** Sets a disabled flag for the whole list. */
    public setDisabled(disabled: boolean): void {
        this._disabled = disabled;
    }

    /** Returns whether the specific path is currently expanded. */
    public isExpanded(pathIndex: string): boolean {
        return this.getExpandedItems().has(pathIndex);
    }

    /** Aggregates expanded paths from all sources: expand-all, initial, and user actions. */
    public getExpandedItems(): Set<string> {
        const expandedPathSet = new Set<string>();
        this._expandAllPaths.forEach((expandedPath) => expandedPathSet.add(expandedPath));
        this._userExpandCollapseSettings.forEach((shouldExpand, path) => {
            if (shouldExpand) {
                expandedPathSet.add(path);
            } else {
                expandedPathSet.delete(path);
            }
        });
        return expandedPathSet;
    }

    public isAllHidden() {
        return this._childNodes.every((itemViewStore) => itemViewStore.isHidden());
    }

    /** Computes tristate for the Select All checkbox across all root items. */
    public getSelectAllState(): TRISTATE_CHECKBOX_VALUES {
        const childNodes = this._childNodes;

        if (!childNodes || childNodes.length === 0) {
            return TRISTATE_CHECKBOX_VALUES.UNCHECKED;
        }

        const totalItems = childNodes.length;
        let checkedItems = 0;
        let partialItems = 0;

        childNodes.forEach((nestedCheckboxItem) => {
            const state = nestedCheckboxItem.calculateParentStateByChildrenValue();
            if (state === TRISTATE_CHECKBOX_VALUES.CHECKED) {
                checkedItems += 1;
            } else if (state === TRISTATE_CHECKBOX_VALUES.PARTIALLY_CHECKED) {
                partialItems += 1;
            }
        });
        if (checkedItems === totalItems) {
            return TRISTATE_CHECKBOX_VALUES.CHECKED;
        }
        if (checkedItems > 0 || partialItems > 0) {
            return TRISTATE_CHECKBOX_VALUES.PARTIALLY_CHECKED;
        }
        return TRISTATE_CHECKBOX_VALUES.UNCHECKED;
    }

    /** Handles clicks on a row: toggles expansion for folders or toggles checkbox for leaves. */
    public handleRowClick(itemViewStore: CheckboxesTreeItemStore, isItemDisabled: boolean, hasChildren: boolean): void {
        if (isItemDisabled && !hasChildren) {
            return;
        }
        const itemId = itemViewStore.pathIndex();
        if (hasChildren) {
            const currentExpandedPaths = new Set<string>(this.getExpandedItems());
            if (currentExpandedPaths.has(itemId)) {
                currentExpandedPaths.delete(itemId);
            } else {
                currentExpandedPaths.add(itemId);
            }
            this.setUserExpandedPaths(currentExpandedPaths);
            return;
        }
        const currentValue = itemViewStore.getCheckBoxValue();
        const nextValue = currentValue === TRISTATE_CHECKBOX_VALUES.CHECKED
            ? TRISTATE_CHECKBOX_VALUES.UNCHECKED
            : TRISTATE_CHECKBOX_VALUES.CHECKED;

        this.handleCheckboxChange(itemViewStore, nextValue);
    }

    /** Applies checkbox state change to a subtree and emits merged data upward. */
    public handleCheckboxChange(itemViewStore: CheckboxesTreeItemStore, newState: TRISTATE_CHECKBOX_VALUES): void {
        itemViewStore.applyCheckboxChange(newState);
        this.emitChanges();
    }

    /** Applies Select All change to all roots and emits merged data upward. */
    public handleSelectAllChange(newState: TRISTATE_CHECKBOX_VALUES): void {
        this._childNodes.forEach((itemViewStore) => itemViewStore.applyCheckboxChange(newState));
        this.emitChanges();
    }

    /** Toggles Select All between CHECKED and UNCHECKED based on current tristate. */
    public toggleSelectAll(): void {
        const currentState = this.getSelectAllState();
        const nextState = (currentState === TRISTATE_CHECKBOX_VALUES.CHECKED || currentState === TRISTATE_CHECKBOX_VALUES.PARTIALLY_CHECKED)
            ? TRISTATE_CHECKBOX_VALUES.UNCHECKED
            : TRISTATE_CHECKBOX_VALUES.CHECKED;

        this.handleSelectAllChange(nextState);
    }

    /** Records user-driven expand/collapse changes with priority over other sources. */
    public setUserExpandedPaths(targetExpandedPaths: Set<string>): void {
        const currentExpandedPaths = this.getExpandedItems();
        const updatedUserExpandCollapseSettings = new Map<string, boolean>(this._userExpandCollapseSettings);

        targetExpandedPaths.forEach((path) => {
            if (!currentExpandedPaths.has(path)) {
                updatedUserExpandCollapseSettings.set(path, true);
            }
        });

        currentExpandedPaths.forEach((path) => {
            if (!targetExpandedPaths.has(path)) {
                updatedUserExpandCollapseSettings.set(path, false);
            }
        });

        this._userExpandCollapseSettings = updatedUserExpandCollapseSettings;
    }

    /** Returns merged data (model + changes) as a plain tree for external consumers. */
    public getUpdatedModel(): ICheckboxesTreeItem[] {
        return this._childNodes.map((itemViewStore) => itemViewStore.getUpdatedModel());
    }

    /** Builds summary text of selected items based on structure, not string parsing. */
    public getSummaryText(): string {
        const itemStores = this.getItems();
        const parts: string[] = [];

        for (const itemStore of itemStores) {
            const label = itemStore.label();
            const hasChildren = itemStore.hasChildren();

            if (!hasChildren) {
                const leafState = itemStore.getCheckBoxValue();
                if (leafState === TRISTATE_CHECKBOX_VALUES.CHECKED) {
                    parts.push(label);
                }
                continue;
            }

            const totalLeaves = itemStore.totalLeafCount;
            const selectedLeaves = itemStore.selectedLeafCount;
            if (selectedLeaves === 0) {
                continue;
            }
            if (selectedLeaves === totalLeaves) {
                parts.push(label + ' (all)');
            } else {
                parts.push(label + ' (' + String(selectedLeaves) + ')');
            }
        }

        return parts.join(', ');
    }

    /** Emits merged items to the onChange callback if provided. */
    private emitChanges(): void {
        const mergedItems = this.getUpdatedModel();
        if (this.onChangeCallback) {
            this.onChangeCallback(mergedItems);
        }
    }

    /** Creates item stores from the plain view tree and indexes them by path (roots only). */
    private initializeItemStores(items: ICheckboxesTreeItem[]): CheckboxesTreeItemStore[] {
        const buildCheckboxItemStores = (viewItem: ICheckboxesTreeItem[]): CheckboxesTreeItemStore[] => {
            return viewItem.map((viewItem, idx) => {
                const pathIndex = `${idx}`;
                return new CheckboxesTreeItemStore(viewItem, pathIndex);
            });
        };
        return buildCheckboxItemStores(items);
    }

    /** Collects all expandable paths when expand-all is active (uses lazy children). */
    private pathsByExpandedAllFromStores(rootStores: CheckboxesTreeItemStore[], enabled: boolean): Set<string> {
        const expandedAllPaths = new Set<string>();
        if (!enabled || !rootStores || rootStores.length === 0) {
            return expandedAllPaths;
        }
        rootStores.forEach((rootStore) => {
            const isNodeDisabled = rootStore.isDisabled() || (rootStore.hasChildren() && rootStore.areAllChildrenDisabled());
            if (isNodeDisabled) {
                return;
            }
            const path = rootStore.pathIndex();
            const hasChildren = rootStore.getChildrenStores().length > 0;
            if (hasChildren) {
                expandedAllPaths.add(path);
                rootStore.getChildrenStores().forEach((childStore) => {
                    const childPaths = this.pathsByExpandedAllFromStores([childStore], true);
                    childPaths.forEach((p) => expandedAllPaths.add(p));
                });
            }
        });
        return expandedAllPaths;
    }

    /** Sets the current filter term and applies filtering with expand-all behavior. */
    public setFilterTerm(term: string): void {
        this._filterTerm = term ?? '';
        this.applyFilter();
    }

    /** Returns whether filtering is currently running. */
    public isFiltering(): boolean {
        return this._isFiltering;
    }

    /** Applies filter to item stores and updates expand-all according to term presence. */
    private applyFilter(): void {
        const term = this._filterTerm.trim().toLowerCase();
        const hasTerm = term !== '';

        const filterNode = (store: CheckboxesTreeItemStore, parentMatches: boolean = false): boolean => {
            const lower = store.labelLower();
            const isSelfMatch = hasTerm ? lower.includes(term) : true;
            const children = store.getChildrenStores();
            let childHasVisible = false;
            if (children && children.length > 0) {
                for (let i = 0; i < children.length; i++) {
                    const visible = filterNode(children[i], parentMatches || isSelfMatch);
                    if (visible) {
                        childHasVisible = true;
                    }
                }
            }
            const isVisible = hasTerm ? (isSelfMatch || childHasVisible || parentMatches) : true;
            store.setHidden(!isVisible);
            return isVisible;
        };

        this._isFiltering = true;
        this._disabled = true;
        runInAction(() => {
            const roots = this._childNodes;
            for (let i = 0; i < roots.length; i++) {
                filterNode(roots[i]);
            }
            this.isAllExpanded(hasTerm);

            this._isFiltering = false;
            this._disabled = false;
        });
    }

    /** Replaces current items with new view items and reapplies expand/filter state. */
    public setItems(items: ICheckboxesTreeItem[]): void {
        this._childNodes = this.initializeItemStores(items);
        const hasTerm = (this._filterTerm.trim().toLowerCase() !== '');
        if (hasTerm) {
            this.applyFilter();
        }
        this.isAllExpanded(hasTerm);
    }
}

export default CheckboxesTreeStore;
